import React, { useEffect, useMemo, useState } from "react";
import {
  KeyRound, ChevronDown, ChevronRight, Lock, Unlock, Play,
  Loader2, Copy, CheckCircle2, XCircle, Server, BookOpen,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  merchantApi, getStoredCredentials, saveCredentials, clearCredentials,
} from "../../services/merchantApiClient";


const LTR = { direction: "ltr", textAlign: "left" };

const SPEC = [
  {
    tag: "Wallets",
    description: "Wallet balances for the authenticated merchant.",
    endpoints: [
      {
        id: "wallets",
        method: "POST",
        path: "/merchant-api/wallets",
        summary: "Get wallet balances",
        description:
          "Returns the primary (Agent) wallet and Commission wallet balances for the authenticated merchant.",
        params: [],
        body: null,
        run: () => merchantApi.getWallets(),
      },
    ],
  },
  {
    tag: "Products",
    description:
      "Product listing endpoints. All accept shared query params (page, limit, search, status, productFor). The Data products endpoint additionally supports `prefix`.",
    endpoints: [
      { id: "allProducts", method: "POST", path: "/merchant-api/all-products",
        summary: "All products",
        description: "Returns every product with the merchant's effective price.",
        params: "shared", body: null,
        run: (q) => merchantApi.getAllProducts(q) },

      { id: "dataProducts", method: "POST", path: "/merchant-api/data-products",
        summary: "Data / Bundle products",
        description: "Returns BUNDLE products with merchant pricing + third-party IDs. Optionally filter by `prefix` (e.g. 78, 79).",
        params: "shared", body: null,
        run: (q) => merchantApi.getDataProducts(q) },

      { id: "gamesProducts", method: "POST", path: "/merchant-api/games-products",
        summary: "Games products",
        description: "Returns GAMES products. Supports comma-separated productCategoryId / productTypeId.",
        params: "shared", body: null,
        run: (q) => merchantApi.getGamesProducts(q) },

      { id: "socialProducts", method: "POST", path: "/merchant-api/social-products",
        summary: "Social products",
        description: "Returns SOCIAL products. Supports comma-separated productCategoryId / productTypeId.",
        params: "shared", body: null,
        run: (q) => merchantApi.getSocialProducts(q) },

      { id: "othersProducts", method: "POST", path: "/merchant-api/others-products",
        summary: "Other products",
        description: "Returns OTHERS products.",
        params: "shared", body: null,
        run: (q) => merchantApi.getOthersProducts(q) },

      { id: "productCategories", method: "GET", path: "/merchant-api/product-categories",
        summary: "Product categories",
        description: "Fetch product categories. Optionally filter by productFor.",
        params: [{ name: "productFor", in: "query", type: "string", description: "TOPUP | BUNDLE | GAMES | SOCIAL | OTHERS" }],
        body: null,
        run: (q) => merchantApi.getProductCategories(q.productFor ? { productFor: q.productFor } : {}) },

      { id: "productTypes", method: "GET", path: "/merchant-api/product-types",
        summary: "Product types",
        description: "Fetch product types. Optionally filter by productFor.",
        params: [{ name: "productFor", in: "query", type: "string", description: "TOPUP | BUNDLE | GAMES | SOCIAL | OTHERS" }],
        body: null,
        run: (q) => merchantApi.getProductTypes(q.productFor ? { productFor: q.productFor } : {}) },
    ],
  },
  {
    tag: "Topup Activation",
    description:
      "Recharge any Afghan mobile number. Use amountType=custom with an amount, or amountType=fixed with a productId.",
    endpoints: [
      {
        id: "activateTopup",
        method: "POST",
        path: "/merchant-api/products-activation",
        summary: "Activate Topup",
        description:
          "bundleType is fixed to TOPUP. For amountType=custom, only `amount` and `receiver` are required. For amountType=fixed, `productId` and matching `amount` are required.",
        params: [],
        body: {
          bundleType: "TOPUP",
          receiver: "",
          amount: "",
          amountType: "custom",
          productId: "",
        },
        run: (_q, body) => merchantApi.activateProduct({
          bundleType: "TOPUP",
          receiver: body.receiver,
          amount: body.amount ? Number(body.amount) : undefined,
          amountType: body.amountType,
          productId: body.productId || undefined,
        }),
      },
    ],
  },
  {
    tag: "Bundle Activation",
    description:
      "Activate a data / voice bundle by productId. The product must have a thirdPartyId configured.",
    endpoints: [
      {
        id: "activateBundle",
        method: "POST",
        path: "/merchant-api/products-activation",
        summary: "Activate Bundle",
        description:
          "bundleType is fixed to BUNDLE. Provide `productId` (the bundle product) and `receiver`. `amount` is not used by the backend for bundles.",
        params: [],
        body: { bundleType: "BUNDLE", receiver: "", productId: "" },
        run: (_q, body) => merchantApi.activateProduct({
          bundleType: "BUNDLE",
          receiver: body.receiver,
          productId: body.productId || undefined,
        }),
      },
    ],
  },
  {
    tag: "Games Activation",
    description:
      "Activate a gaming product. Provide the game productId and the player / account ID as `receiver`.",
    endpoints: [
      {
        id: "activateGame",
        method: "POST",
        path: "/merchant-api/products-activation",
        summary: "Activate Game",
        description:
          "bundleType is fixed to GAMES. Provide `productId` (the game product) and `receiver` (player / account ID). `amount` and `amountType` are not used by the backend for games.",
        params: [],
        body: { bundleType: "GAMES", receiver: "", productId: "" },
        run: (_q, body) => merchantApi.activateProduct({
          bundleType: "GAMES",
          receiver: body.receiver,
          productId: body.productId || undefined,
        }),
      },
    ],
  },
  {
    tag: "Social Activation",
    description:
      "Activate a social product (e.g. Facebook, TikTok, etc.). Provide the social productId and `receiver`.",
    endpoints: [
      {
        id: "activateSocial",
        method: "POST",
        path: "/merchant-api/products-activation",
        summary: "Activate Social",
        description:
          "bundleType is fixed to SOCIAL. Provide `productId` (the social product) and `receiver`. `amount` and `amountType` are not used by the backend for social.",
        params: [],
        body: { bundleType: "SOCIAL", receiver: "", productId: "" },
        run: (_q, body) => merchantApi.activateProduct({
          bundleType: "SOCIAL",
          receiver: body.receiver,
          productId: body.productId || undefined,
        }),
      },
    ],
  },
  {
    tag: "Orders",
    description: "Order listing and status endpoints.",
    endpoints: [
      {
        id: "orders",
        method: "GET",
        path: "/merchant-api/orders",
        summary: "List orders",
        description:
          "Lists orders for the authenticated merchant. Supports page, limit, status (pending|succeeded|failed|inprocess), type (recharge|bundle|games|social), receiver, txnNumber, fromDate, toDate, sortBy, sortOrder.",
        params: "shared",
        body: null,
        run: (q) => merchantApi.getOrders({
          page: q.page,
          limit: q.limit,
          status: q.status || undefined,
          search: q.search || undefined,
        }),
      },
      {
        id: "orderStatus",
        method: "GET",
        path: "/merchant-api/order-status/{transactionId}",
        summary: "Order status",
        description: "Fetch a single order by transaction number.",
        params: [{ name: "transactionId", in: "path", type: "string", description: "TXN-xxxxxxxxx-xxx" }],
        body: null,
        run: (_q, _b, extra) => merchantApi.checkOrderStatus(extra.transactionId),
      },
    ],
  },
];

const AuthorizeBar = ({ onSave, onClear, credentials }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [apiKey, setApiKey] = useState(credentials?.apiKey || "");
  const [secretKey, setSecretKey] = useState(credentials?.secretKey || "");

  const submit = (e) => {
    e.preventDefault();
    const creds = { apiKey: apiKey.trim(), secretKey: secretKey.trim() };
    if (!creds.apiKey || !creds.secretKey) return;
    saveCredentials(creds);
    onSave(creds);
    setOpen(false);
  };

  const clear = () => {
    clearCredentials();
    setApiKey("");
    setSecretKey("");
    onClear();
  };

  return (
    <div className="border-b-2 border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {credentials ? (
              <Unlock className="text-green-600" size={20} />
            ) : (
              <Lock className="text-amber-600" size={20} />
            )}
            <div>
              <h1 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {credentials
                  ? t("merchantApi.authorized")
                  : t("merchantApi.unauthorized")}
              </h1>
              <p
                dir="ltr"
                className="text-xs text-gray-500 dark:text-gray-400 font-mono"
                style={LTR}
              >
                {credentials
                  ? t("merchantApi.credentialsMask", {
                      prefix: credentials.apiKey.slice(0, 10),
                      suffix: credentials.apiKey.slice(-4),
                    })
                  : t("merchantApi.provideCredentials")}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {credentials && (
              <button
                onClick={clear}
                className="px-3 py-2 text-sm rounded-md border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                {t("merchantApi.logout")}
              </button>
            )}
            <button
              onClick={() => setOpen((o) => !o)}
              className="px-4 py-2 text-sm rounded-md bg-[#E59213] text-white flex items-center gap-2"
            >
              <KeyRound size={14} />
              {credentials
                ? t("merchantApi.updateCredentials")
                : t("merchantApi.authorize")}
            </button>
          </div>
        </div>

        {open && (
          <form
            onSubmit={submit}
            className="mt-4 p-4 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
          >
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  {t("merchantApi.apiKeyLabel")}
                </label>
                <input
                  dir="ltr"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  type="text"
                  placeholder={t("merchantApi.apiKeyPlaceholder")}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-mono dark:text-white"
                  style={LTR}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                  {t("merchantApi.secretKeyLabel")}
                </label>
                <input
                  dir="ltr"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  type="password"
                  placeholder={t("merchantApi.secretKeyPlaceholder")}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-mono dark:text-white"
                  style={LTR}
                />
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                type="submit"
                className="px-4 py-2 text-sm rounded-md bg-gradient-to-r from-[#E59213] to-primary text-white"
              >
                {t("merchantApi.authorize")}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200"
              >
                {t("common.cancel")}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t("merchantApi.credentialsNote")}
            </p>
          </form>
        )}
      </div>
    </div>
  );
};


const methodColor = {
  GET:    "bg-blue-600",
  POST:   "bg-emerald-600",
  PATCH:  "bg-amber-600",
  PUT:    "bg-amber-600",
  DELETE: "bg-red-600",
};


const EndpointRow = ({ endpoint, authorized, sharedQuery, setSharedQuery }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const [response, setResponse] = useState(null);
  const [body, setBody] = useState(endpoint.body ? { ...endpoint.body } : null);
  const [pathParam, setPathParam] = useState("");
  const [localQuery, setLocalQuery] = useState({});

  const usesShared = endpoint.params === "shared";
  const paramList  = Array.isArray(endpoint.params) ? endpoint.params : [];
  const supportsPrefix = endpoint.id === "dataProducts";

  const buildQuery = () => {
    if (usesShared) {
      const q = { page: sharedQuery.page, limit: sharedQuery.limit };
      if (sharedQuery.search) q.search = sharedQuery.search;
      if (sharedQuery.status) q.status = sharedQuery.status;
      if (sharedQuery.productFor) q.productFor = sharedQuery.productFor;
      if (supportsPrefix && sharedQuery.prefix) q.prefix = sharedQuery.prefix;
      return q;
    }
    const q = {};
    paramList.forEach((p) => {
      if (p.in === "query" && localQuery[p.name]) q[p.name] = localQuery[p.name];
    });
    return q;
  };

  const send = async () => {
    setRunning(true);
    setResponse(null);
    try {
      const extra = {};
      if (paramList.some((p) => p.in === "path")) extra.transactionId = pathParam;
      const res = await endpoint.run(buildQuery(), body, extra);
      setResponse({ ok: true, status: res?.status || 200, data: res?.data });
    } catch (e) {
      setResponse({
        ok: false,
        status: e?.response?.status || 500,
        data: e?.response?.data || { error: e.message },
      });
    } finally {
      setRunning(false);
    }
  };

  const copyCurl = () => {
    const creds = getStoredCredentials() || {};
    const query = buildQuery();
    const qs = Object.keys(query).length
      ? "?" + new URLSearchParams(query).toString()
      : "";
    const path = endpoint.path.replace(
      "{transactionId}",
      pathParam || "{transactionId}"
    );
    let curl = `curl -X ${endpoint.method} "https://<your-host>${path}${qs}" \\\n`;
    curl += `  -H "x-api-key: ${creds.apiKey || "YOUR_API_KEY"}" \\\n`;
    curl += `  -H "x-secret-key: ${creds.secretKey || "YOUR_SECRET_KEY"}"`;
    if (body) {
      curl += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body)}'`;
    }
    navigator.clipboard.writeText(curl);
  };

  const sharedParamKeys = supportsPrefix
    ? ["page", "limit", "search", "status", "productFor", "prefix"]
    : ["page", "limit", "search", "status", "productFor"];

  return (
    <div className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
      >
        <span
          className={`text-xs font-bold text-white px-3 py-1 rounded ${methodColor[endpoint.method]} w-[62px] text-center`}
          dir="ltr"
          style={LTR}
        >
          {endpoint.method}
        </span>
        <code
          dir="ltr"
          className="text-sm font-mono text-gray-900 dark:text-gray-100 flex-1 truncate"
          style={LTR}
        >
          {endpoint.path}
        </code>
        <span className="hidden md:block text-xs text-gray-500 dark:text-gray-400 truncate max-w-[260px]">
          {t(`merchantApi.endpoints.${endpoint.id}.summary`, endpoint.summary)}
        </span>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>

      {open && (
        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-4 py-4 space-y-4">
          {endpoint.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t(
                `merchantApi.endpoints.${endpoint.id}.description`,
                endpoint.description
              )}
            </p>
          )}

          {usesShared && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-2">
                {t("merchantApi.sharedQueryParams")}
              </h4>
              <div className={`grid grid-cols-2 gap-2 ${supportsPrefix ? "md:grid-cols-6" : "md:grid-cols-5"}`}>
                {sharedParamKeys.map((k) => (
                  <div key={k}>
                    <label
                      dir="ltr"
                      className="block text-[11px] text-gray-500 dark:text-gray-400 mb-0.5 font-mono"
                      style={LTR}
                    >
                      {t(`merchantApi.queryParams.${k}`, k)}
                    </label>
                    <input
                      dir="ltr"
                      value={sharedQuery[k] || ""}
                      onChange={(e) =>
                        setSharedQuery({ ...sharedQuery, [k]: e.target.value })
                      }
                      placeholder={k === "prefix" ? "e.g. 78" : ""}
                      className="w-full h-9 px-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-xs dark:text-white"
                      style={LTR}
                    />
                  </div>
                ))}
              </div>
              {supportsPrefix && (
                <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
                  {t(
                    "merchantApi.prefixHint",
                    "prefix filters bundles by the leading digits of the receiver number (e.g. 78, 79)."
                  )}
                </p>
              )}
            </div>
          )}

          {paramList.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-2">
                {t("merchantApi.parameters")}
              </h4>
              <div className="grid md:grid-cols-2 gap-2">
                {paramList.map((p) => (
                  <div key={p.name}>
                    <label
                      dir="ltr"
                      className="block text-[11px] text-gray-500 dark:text-gray-400 mb-0.5 font-mono"
                      style={LTR}
                    >
                      {p.name}{" "}
                      <span className="text-gray-400">
                        ({t(`merchantApi.paramLocation.${p.in}`, p.in)})
                      </span>
                    </label>
                    <input
                      dir="ltr"
                      value={
                        p.in === "path" ? pathParam : localQuery[p.name] || ""
                      }
                      onChange={(e) =>
                        p.in === "path"
                          ? setPathParam(e.target.value)
                          : setLocalQuery({
                              ...localQuery,
                              [p.name]: e.target.value,
                            })
                      }
                      placeholder={p.description || ""}
                      className="w-full h-9 px-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-xs font-mono dark:text-white"
                      style={LTR}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {body && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase mb-2">
                {t("merchantApi.requestBody")}
              </h4>
              <textarea
                dir="ltr"
                value={JSON.stringify(body, null, 2)}
                onChange={(e) => {
                  try {
                    setBody(JSON.parse(e.target.value));
                  } catch {
                    /* ignore while typing */
                  }
                }}
                rows={8}
                spellCheck={false}
                className="w-full p-3 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-xs font-mono dark:text-white"
                style={{
                  direction: "ltr",
                  textAlign: "left",
                  unicodeBidi: "plaintext",
                }}
              />
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={send}
              disabled={!authorized || running}
              className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 text-white ${
                !authorized ? "bg-gray-400 cursor-not-allowed" : "bg-[#E59213]"
              }`}
            >
              {running ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Play size={14} />
              )}
              {t("merchantApi.execute")}
            </button>
            <button
              onClick={copyCurl}
              className="px-3 py-2 rounded-md text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 flex items-center gap-1.5"
            >
              <Copy size={14} /> {t("merchantApi.copyCurl")}
            </button>
            {!authorized && (
              <span className="text-xs text-amber-600 dark:text-amber-400">
                {t("merchantApi.authorizeFirst")}
              </span>
            )}
          </div>

          {response && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                {response.ok ? (
                  <CheckCircle2 size={16} className="text-green-600" />
                ) : (
                  <XCircle size={16} className="text-red-600" />
                )}
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {t("merchantApi.response")} · HTTP {response.status}
                </span>
              </div>
              <pre
                dir="ltr"
                className="text-xs bg-gray-900 text-green-300 p-3 rounded-lg overflow-auto max-h-96 font-mono"
                style={{
                  direction: "ltr",
                  textAlign: "left",
                  unicodeBidi: "plaintext",
                }}
              >
{JSON.stringify(response.data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main Component                                                      */
/* ------------------------------------------------------------------ */
export default function MerchantApiUAT() {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState(getStoredCredentials());
  const [sharedQuery, setSharedQuery] = useState({ page: 1, limit: 10 });

  useEffect(() => {
    const onStorage = () => setCredentials(getStoredCredentials());
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const totalEndpoints = useMemo(
    () => SPEC.reduce((n, g) => n + g.endpoints.length, 0),
    []
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AuthorizeBar
        credentials={credentials}
        onSave={setCredentials}
        onClear={() => setCredentials(null)}
      />

      <div id="spec" className="max-w-6xl mx-auto px-4 py-6 space-y-8">
        {SPEC.map((group) => (
          <section key={group.tag}>
            <div className="mb-3">
              <h2 className="text-base font-bold text-gray-900 dark:text-white">
                {t(`merchantApi.groups.${group.tag}.title`, group.tag)}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t(
                  `merchantApi.groups.${group.tag}.description`,
                  group.description
                )}
              </p>
            </div>
            <div className="space-y-2">
              {group.endpoints.map((ep) => (
                <EndpointRow
                  key={ep.id}
                  endpoint={ep}
                  authorized={!!credentials}
                  sharedQuery={sharedQuery}
                  setSharedQuery={setSharedQuery}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}